import type { ParseGroupHook, SerializeGroupHook } from '@customerio/dryad';

/**
 * The grammar of a flat conditions wire: a bare array of `type`-tagged leaves,
 * joined by an implicit AND.
 *
 * The backend decodes these with a plain array read and has no group case, so
 * there is nothing for a group envelope to mean: an `{ or: [...] }` is refused
 * in both directions, and no surface on this wire can build a nested group
 * (every one of them caps the builder at a single level). Negation is a leaf's
 * own business — a prefixed operator, or a field the leaf carries — never a
 * wrapper around a group.
 *
 * Shared because it describes bytes rather than choices: two surfaces storing
 * this wire have to read it the same way, or a condition written in one means
 * something else in the other. Which leaves a surface claims is still its own,
 * declared by its plugin list.
 */

/**
 * Reads the wire into a root group.
 *
 * A bare array is the wire's own spelling. An `{ and: [...] }` envelope is the
 * same thing wearing a coat, and some stored values carry one, so it is
 * unwrapped on the way in. Anything else is declined, which hands it to
 * dryad's built-in grammar: the children survive as individual rows and the
 * envelope is dropped on the way out, rather than being written back to a
 * decoder that would refuse it.
 */
export const flatFilterParseGroup: ParseGroupHook = (json, builtInGroup) => {
  if (Array.isArray(json)) {
    return { operator: 'and', children: json };
  }
  const group = builtInGroup(json);
  return group?.operator === 'and' ? group : null;
};

/** Writes the root group back as the bare array the wire is. */
export const flatFilterSerializeGroup: SerializeGroupHook = (_operator, children) => children;
